// ═══════════════════════════════════════════════════════════════════════════
//  HeaderStatsBar — the strip under the page header: running bots, today's P&L,
//  trades today, win rate, and the derived signal summary.
//
//  Lifted out of LiveTrading.tsx (God 2026-08-26, phase 3 — the last section).
//  JSX byte-identical; the sixteen values it closed over now arrive as props.
//  The list came from the compiler: hosted with no props, tsc named exactly the
//  free identifiers — 4 icons, 3 foundation helpers, 16 props.
//
//  Every number here is DERIVED by the page and passed in, not recomputed. Two
//  places computing the same figure is how this system produced a card showing
//  one P&L while the panel beside it showed another.
//
//  Rendered by both lanes: on KALI these are the LIVE ledger's numbers, on
//  DUMABATI the paper ledger's — same component, different binding.
// ═══════════════════════════════════════════════════════════════════════════
import React from 'react';
import { Activity, BarChart2, Target, TrendingUp } from 'lucide-react';
import { LiveSignal, Deployment, fmtM, pCol } from './foundation';

/** Declared inline, not imported: the original lives INSIDE the LiveTrading
 *  component body (a local interface), so there is nothing in foundation to
 *  import. Copied field-for-field rather than widened to `any` — the shape is
 *  the contract with /trades/summary and narrowing it later would be silent. */
export interface BackendSummary {
  running_bots: number; total_pnl: number; trades_today: number; win_rate: number;
  wins: number; losses: number; open_positions: number; last_updated: string;
}

export interface HeaderStatsBarProps {
  /** Opens the Deep P&L drawer. Supplied only by the pages that host it;
   *  when absent the P&L tile keeps its original trade-report behaviour,
   *  so this prop can never break a caller that does not know about it. */
  onOpenPnlDrawer?: () => void;
  /** "Today" or the session's date ("23 Sep") — the window every session tile
   *  reads (God 2026-09-24: the calendar day showed zeros all night). */
  sessionLabelText?: string;
  /** Epoch ms of the last successful backend sync; printed on the strip so a
   *  number frozen by an outage is never mistaken for a live one. */
  lastSyncAt?: number;
  backendConnected: boolean;
  backendOpenTrades: any[];
  backendSummary: BackendSummary | null;
  liveSignals: LiveSignal[];
  runningDeps: Deployment[];
  sigCounts: { signals: number; executed: number; skipped: number; buy: number; sell: number; exit: number } | null;
  /** SKIPPED CENSUS LEDGER (God 2026-10-05 18:04): the skipped pill opens the Symbol · TF · Reason ledger of
   *  this lane. Optional so the component still renders where no ledger is mounted. */
  onSkippedClick?: () => void;
  isMobile: boolean;
  tradingMode: 'PAPER' | 'LIVE';
  /** All derived by the page — never recomputed in this component. */
  todayPnl: number;
  totalPnl: number;
  totalTrades: number;
  tradesToday: number;
  winRate: string;
  setReportTab: React.Dispatch<React.SetStateAction<'PAPER'|'LIVE'|'OPEN'>>;
  setShowHistoryModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** P&L TRACKING (God Phase 3, 2026-09-24): running M2M of the open rows,
   *  computed by the page through pnlMath.sumM2M from the contracts' live LTPs.
   *  `null` = open rows exist but none has a live price yet; the tile then says
   *  so instead of printing 0. `openPriced`/`openCount` label the coverage. */
  runningM2M?: number | null;
  openPriced?: number;
  openCount?: number;
}

export const HeaderStatsBar: React.FC<HeaderStatsBarProps> = ({
  backendConnected, backendOpenTrades, backendSummary, liveSignals, runningDeps,
  sigCounts, isMobile, tradingMode, todayPnl, totalPnl, totalTrades, tradesToday,
  winRate, setReportTab, setShowHistoryModal, onOpenPnlDrawer, onSkippedClick, sessionLabelText, lastSyncAt,
  runningM2M, openPriced, openCount,
}) => (
  <>
      {/* STATS BAR */}
      {(() => {
        // Prefer live backend numbers, but when the backend reports 0 (it doesn't
        // track the frontend-deployed bridge bots) fall back to the deployment
        // aggregates so the cards never show a false 0.
        // All numbers derive from the SAME canonical-ledger activeTrades the report
        // uses → cards and report footer reconcile by construction.
        const liveRunningBots  = runningDeps.length;
        const livePnl          = totalPnl;                                  // all-time (this mode)
        const liveTradesCount  = tradesToday;                              // explicit today-IST window
        const liveWinRateStr   = totalTrades > 0 ? `${winRate}%` : '—';    // all-time
        const liveOpenPos      = backendOpenTrades.length;
        const sess = sessionLabelText || 'Today';
        const syncAge = lastSyncAt ? Math.round((Date.now() - lastSyncAt) / 1000) : null;
        const syncNote = lastSyncAt
          ? `as of ${new Date(lastSyncAt).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}${syncAge != null && syncAge > 30 ? ` · ${syncAge}s stale` : ''}`
          : (backendConnected ? 'syncing…' : 'OFFLINE — numbers may be stale');
        // Booked (todayPnl) · running M2M · total — the second line of the P&L
        // tile. Only drawn while something is open; never a 0 for "unknown".
        const _open = openCount ?? 0;
        const _m2mTxt = _open === 0 ? '' : runningM2M == null ? 'M2M — (no live tick)'
          : `M2M ${runningM2M >= 0 ? '+' : '-'}₹${fmtM(Math.abs(runningM2M))}${(openPriced ?? 0) < _open ? ` (${openPriced}/${_open})` : ''}`;
        const _total = todayPnl + (runningM2M ?? 0);
        const _totalTxt = _open === 0 ? '' : ` · Total ${_total >= 0 ? '+' : '-'}₹${fmtM(Math.abs(_total))}`;
        const _m2mTip = _open === 0 ? '' : runningM2M == null
          ? `${_open} open position${_open === 1 ? '' : 's'}, none has a live contract price yet`
          : `big number = BOOKED (realised, ${sess === 'Today' ? 'today' : sess}) · running M2M = Σ (live LTP − entry) × openQty × dir over ${openPriced}/${_open} priced open rows · Total = booked + M2M`;
        return (
          <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:isMobile?8:10,padding:isMobile?'8px 12px':'8px 20px',background:'#0d1117',borderBottom:'1px solid #1a1f3a'}}>
            {[
              {label:`Running Bots${liveOpenPos ? ` · ${liveOpenPos} OPEN` : ''}`, val:liveRunningBots,         icon:Activity,   col:'#10b981', sub:syncNote},
              // The outside stays one number; the history lives in the drawer.
              // SESSION label (God 2026-09-24): "Today's" once the market has
              // opened, else the session's date — never a zero for a day that
              // has not started.
              {label:`${sess === 'Today' ? "Today's" : sess} PnL [${tradingMode}]`, val:`${todayPnl>=0?'+':''}₹${fmtM(todayPnl)}`, icon:TrendingUp, col:pCol(todayPnl), sub:`Active Pine: ₹${fmtM(livePnl)}`, subCol:pCol(livePnl), onClick:onOpenPnlDrawer,
               sub2: _m2mTxt ? `${_m2mTxt}${_totalTxt}` : '', sub2Col: runningM2M == null ? '#64748b' : pCol(_total), sub2Tip: _m2mTip},
              {label:`Trades ${sess === 'Today' ? 'Today' : '· ' + sess}`,         val:liveTradesCount,          icon:BarChart2,   col:'#60a5fa', sub:`Active Pine: ${totalTrades}`},
              {label:'Win Rate',                                                   val:liveWinRateStr,           icon:Target,      col:'#fbbf24', sub:'active Pine v6'},
            ].map((s,i)=>(
              <div key={i} className="stat-card" style={{color:s.col}} onClick={s.onClick ? s.onClick : ()=>{setReportTab(tradingMode); setShowHistoryModal(true);}} title={s.onClick ? 'Click for Deep P&L Analytics — daily, weekly, monthly, yearly' : (backendConnected ? `Live · ${backendSummary?.last_updated ?? ''}` : 'Click to view full Trade Report')}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                  <div style={{fontSize:9,color:'#64748b',fontWeight:700,textTransform:'uppercase',display:'flex',alignItems:'center',gap:6}}>
                    {s.label}
                    {/* This chip means "the backend is streaming these numbers",
                        NOT the LIVE lane. In this system LIVE means real money,
                        so a green LIVE chip on the PAPER page reads as the wrong
                        lane — which is exactly how the 04-Sep zero-tiles report
                        started. Say what it actually means. */}
                    {backendConnected && <span style={{fontSize:7,padding:'1px 4px',borderRadius:3,background:'rgba(16,185,129,.15)',color:'#10b981',letterSpacing:.5}}>SYNC</span>}
                  </div>
                  <s.icon size={14} color={s.col}/>
                </div>
                <div style={{fontSize:16,fontWeight:800,color:s.col,display:'flex',alignItems:'baseline',gap:8}}>
                  {s.val}
                  {s.sub && <span style={{fontSize:9,fontWeight:700,color:(s as any).subCol||'#64748b'}}>{s.sub}</span>}
                </div>
                {(s as any).sub2 && (
                  <div title={(s as any).sub2Tip} style={{fontSize:8.5,fontWeight:800,color:(s as any).sub2Col||'#64748b',marginTop:3,fontFamily:'monospace',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {(s as any).sub2}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* TODAY'S SIGNAL SUMMARY — derived from live signal stream */}
      {(() => {
        // The counts come from the SESSION window (sigCounts, same as the tiles
        // above), so the label must name that session - not the calendar day.
        // Before the 09:00 bell it read "TODAY 25 SEP" over the 24th's counts.
        const pastSession = !!sessionLabelText && sessionLabelText !== 'Today';
        const todayStr = pastSession
          ? `· ${String(sessionLabelText).toUpperCase()}`
          : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase();
        const stripLabel = pastSession ? 'SESSION' : 'TODAY';
        // Prefer the backend signal-accounting (true EXECUTED/SKIPPED) over the raw stream.
        const buy   = sigCounts ? sigCounts.buy  : liveSignals.filter(s => /BUY/i.test(s.action)).length;
        const sell  = sigCounts ? sigCounts.sell : liveSignals.filter(s => /SELL/i.test(s.action)).length;
        const exit  = sigCounts ? sigCounts.exit : liveSignals.filter(s => /EXIT|CLOSE|SQUARE/i.test(s.action)).length;
        const total = sigCounts ? sigCounts.signals  : liveSignals.length;
        const exec  = sigCounts ? sigCounts.executed : liveSignals.filter(s => s.status === 'EXECUTED').length;
        const failed = sigCounts ? sigCounts.skipped : liveSignals.filter(s => s.status === 'FAILED').length;
        return (
          <div style={{display:'flex',alignItems:'center',gap:0,padding:'0 20px',background:'#080c14',borderBottom:'1px solid #1a1f3a',flexWrap:'wrap'}}>
            <div style={{fontSize:9,color:'#334155',fontWeight:800,letterSpacing:1,padding:'8px 14px 8px 0',borderRight:'1px solid #1a2138',marginRight:14,whiteSpace:'nowrap'}}>
              {stripLabel} {todayStr}
            </div>
            {[
              { emoji:'🟢', label:'BUY',  val: buy,  col:'#10b981' },
              { emoji:'🔴', label:'SELL', val: sell, col:'#f87171' },
              { emoji:'⚪', label:'EXIT', val: exit, col:'#94a3b8' },
            ].map(({ emoji, label, val, col }) => (
              <div key={label} style={{display:'flex',alignItems:'center',gap:6,padding:'8px 18px 8px 0',marginRight:4}}>
                <span style={{fontSize:11}}>{emoji}</span>
                <span style={{fontSize:9,color:'#475569',fontWeight:700,letterSpacing:.5}}>{label}</span>
                <span style={{fontSize:15,fontWeight:900,color:col,fontFamily:'monospace',minWidth:18,textAlign:'center'}}>{val}</span>
              </div>
            ))}
            <div style={{width:1,height:28,background:'#1a2138',margin:'0 14px 0 8px'}}/>
            <div style={{display:'flex',gap:14,alignItems:'center'}}>
              <span style={{fontSize:9,color:'#475569'}}><span style={{color:'#60a5fa',fontWeight:900}}>{total}</span> signals</span>
              <span style={{fontSize:9,color:'#475569'}}><span style={{color:'#10b981',fontWeight:900}}>{exec}</span> executed</span>
              {(failed > 0 || onSkippedClick) && (
                <button onClick={onSkippedClick} disabled={!onSkippedClick}
                  title={onSkippedClick ? 'open the SKIPPED CENSUS LEDGER (Symbol · TF · Reason)' : 'signals received but not executed (with reason in console)'}
                  style={{background:'transparent',border:onSkippedClick ? '1px solid #fbbf2455' : 'none',borderRadius:999,padding:onSkippedClick ? '3px 9px' : 0,
                    fontSize:9,color:'#fbbf24',fontWeight:800,cursor:onSkippedClick ? 'pointer' : 'default',minHeight:onSkippedClick ? 24 : undefined}}>
                  {failed} skipped{onSkippedClick ? ' ▸' : ''}
                </button>
              )}
            </div>
            {total === 0 && (
              <span style={{fontSize:8,color:'#334155',marginLeft:8}}>— waiting for webhook signals —</span>
            )}
          </div>
        );
      })()}
  </>
);

export default HeaderStatsBar;
